/** biome-ignore-all lint/suspicious/noExplicitAny: temporary */
import dayjs from 'dayjs';
import { Area, Bar, CartesianGrid, ComposedChart, Legend, Line, Tooltip, XAxis, YAxis } from 'recharts';

import { type AspectPolicy, useAspectStyle } from '#core/components/dashboard/dashboard-grid.utils.ts';
import { STROKE_DASHARRAYS } from '#core/components/dashboard/panels/chart/constants.ts';
import { mantineColorVar } from '#core/lib/formatters/colors.ts';
import { formatNumber, inferNumberFormat } from '#core/lib/formatters/number-decimals.ts';

import { createGridComponent } from '../shared/helpers.ts';
import classes from './Cartesian.module.css';
import { CartesianSkeleton } from './CartesianSkeleton.tsx';
import { CartesianSchema } from './schema.ts';

export const Cartesian = createGridComponent(
  ({ datasets, ...props }) => {
    const aspectStyle = useAspectStyle(aspectPolicy);

    const mappedData = props.series.flatMap((s) =>
      datasets.get(s.inputDataset)?.rows.map((row: any) => ({
        x: dayjs(row[s.data.x]).valueOf(),
        [s.id]: row[s.data.y] ?? null,
        [`${s.id}__min`]: s.data.min ? row[s.data.min] : null,
        [`${s.id}__max`]: s.data.max ? row[s.data.max] : null,
      })),
    );
    const data = [
      ...Map.groupBy(mappedData, (row) => row.x)
        .entries()
        .map(([x, rows]) => ({
          x,
          ...rows.reduce((acc, row) => ({ ...acc, ...row }), {}),
        })),
    ];

    const combinedValues = data.flatMap((row) =>
      props.series.map((name) => row[name as any]).filter((v): v is number => typeof v === 'number'),
    );
    const valueFormatter = (value: number | string | readonly (string | number)[] | undefined): string => {
      if (value === undefined) {
        return 'n/a';
      }
      if (typeof value === 'string') {
        return value;
      }
      if (Array.isArray(value)) {
        return value.map(valueFormatter).join(', ');
      }
      return formatNumber(value as number, { ...inferNumberFormat(combinedValues), useThousandSeparator: true });
    };
    const labelFormatter = (value: number | string | React.ReactNode | undefined) => {
      if (value === undefined) {
        return 'n/a';
      }
      if (typeof value === 'number') {
        return dayjs(value).format('YY-MM-DD');
      }
      return value?.toString() ?? 'n/a';
    };

    return (
      <div className={classes.cartesianChart} style={aspectStyle}>
        <ComposedChart className={classes.chart} data={data} responsive>
          <CartesianGrid stroke={mantineColorVar('dark', 5)} strokeOpacity={0.5} vertical={false} yAxisId="left" />
          <XAxis
            axisLine={false}
            dataKey="x"
            fontSize={12}
            fontWeight={500}
            label={{
              value: props.xAxis?.label,
              offset: -5,
              position: 'bottom',
              fontSize: 'var(--mantine-font-size-xs)',
              fill: mantineColorVar('dark', 1),
            }}
            minTickGap={10}
            stroke={mantineColorVar('dark', 0)}
            tickFormatter={labelFormatter}
          />
          <YAxis
            axisLine={false}
            fontSize={12}
            fontWeight={500}
            label={{
              value: props.yAxisLeft?.label,
              offset: 5,
              position: 'insideLeft',
              fontSize: 'var(--mantine-font-size-xs)',
              fill: mantineColorVar('dark', 1),
              angle: -90,
              style: { textAnchor: 'middle' },
            }}
            stroke={mantineColorVar('dark', 0)}
            tickFormatter={valueFormatter}
            width="auto"
            yAxisId="left"
          />
          <YAxis
            axisLine={false}
            fontSize={12}
            fontWeight={500}
            label={{
              value: props.yAxisRight?.label,
              position: 'insideRight',
              fontSize: 'var(--mantine-font-size-xs)',
              fill: mantineColorVar('dark', 1),
              angle: -90,
              style: { textAnchor: 'middle' },
            }}
            orientation="right"
            stroke={mantineColorVar('dark', 0)}
            tickFormatter={valueFormatter}
            width="auto"
            yAxisId="right"
          />
          <Legend
            align="right"
            formatter={(value) => <span style={{ fontSize: 'var(--mantine-font-size-sm)' }}>{value}</span>}
            iconSize={8}
            verticalAlign="bottom"
          />
          <Tooltip
            allowEscapeViewBox={{ x: false, y: false }}
            // axisId={'left'}
            contentStyle={undefined}
            cursor={{ stroke: 'var(--mantine-color-dark-2)', strokeOpacity: 0.5 }}
            formatter={valueFormatter}
            isAnimationActive={false}
            itemStyle={undefined}
            labelFormatter={labelFormatter}
            labelStyle={undefined}
            separator={undefined}
            wrapperClassName={classes.tooltip}
            wrapperStyle={undefined}
          />
          {props.series.map((s) => {
            const type = props.series.length === 1 && (s.type === 'line' || !s.type) ? 'area' : (s.type ?? 'line');
            const Component = cartesianComponent[type];
            const color = s.color ?? 'blue';

            return (
              <>
                {!!(s.data.min || s.data.max) && <Area dataKey={s.id} />}
                <Component
                  activeDot={{
                    r: 4,
                    fill: mantineColorVar(color, 9),
                    fillOpacity: 1,
                    strokeWidth: 1.5,
                    stroke: mantineColorVar(color, 3),
                  }}
                  connectNulls={false}
                  dataKey={s.id}
                  dot={{ r: 2, fill: mantineColorVar(color, 9), fillOpacity: 1, strokeWidth: 1.5 }}
                  fill={mantineColorVar(color, 8)}
                  fillOpacity={fillOpacity.light}
                  isAnimationActive={false}
                  key={s.id}
                  legendType="square"
                  name={s.label}
                  stroke={mantineColorVar(color, 3)}
                  strokeDasharray={STROKE_DASHARRAYS[s.strokeStyle ?? 'solid']}
                  strokeWidth={1.5}
                  type={curve[s.curve ?? 'linear']}
                  unit={props.yAxisLeft?.unit}
                  yAxisId={s.axis ?? 'left'}
                />
              </>
            );
          })}
          {/* {props.referenceLines?.map((r) => ())} */}
        </ComposedChart>
      </div>
    );
  },
  CartesianSchema,
  CartesianSkeleton,
);

const curve = {
  smooth: 'monotone',
  linear: 'linear',
  step: 'step',
} as const;

const fillOpacity = {
  light: 0.15,
  medium: 0.3,
  strong: 0.6,
  none: 0,
} as const;

const aspectPolicy: AspectPolicy = {
  preferredAspect: 2,
  minAspect: 1.5,
  maxAspect: 3.0,
  minHeight: 300,
  maxHeight: 400,
} as const;

const cartesianComponent = {
  area: Area,
  bar: Bar,
  line: Line,
} as const;
